import { Spin } from "antd";
import i18n from "i18next";
import type { FC } from "react";
import { useCallback, useState } from "react";
import { useDispatch } from "react-redux";

import type { ContractAppProfileEntity } from "src/app/features/Contract/types/subTypes/ContractAppProfileEntity";
import { FormManager } from "../../../../../components/Form/FormManager";
import { useFormSubmitCompletion } from "../../../../../components/Form/hooks/useFormSubmitCompletion";
import { useLoadFormDefinitionQuery } from "../../../../../components/Form/services/server/serverApi";
import { FormLayoutType } from "../../../../../components/Form/types";
import type { FormProps } from "../../../../../components/Form/types/FormProps";
import { showWarningNotification } from "../../../../../components/NotificationAlerts";
import { ResourceName } from "../../../../../core/router/config/ResourceName";
import { BASE_PATH, FORM_ACTION } from "../../../../../core/router/types/routerTypes";
import { useContractProfileSaveMutation } from "../../services/server/ContractProfileApi";
import type { ContractProfileEntity } from "../../types/ContractProfileEntity";

export const ContractProfileCreateForm: FC<FormProps> = ({ runSubmit, formAction }) => {
    const { data: formDefinition, isFetching: configIsFetching } = useLoadFormDefinitionQuery("contract-profile");

    const [formData, setFormData] = useState<ContractProfileEntity>({} as ContractProfileEntity);
    const onChangeCallback = useCallback(setFormData, [setFormData]);

    const dispatch = useDispatch();
    const completeSubmit = useFormSubmitCompletion(formAction, `${BASE_PATH}/${ResourceName.CONTRACT_PROFILES}`);
    const [saveFormData, { isLoading: saveFormIsInProgress }] = useContractProfileSaveMutation();

    const shouldSaveContractAppProfile = ({ acceptMode, routingId, readOnly }: ContractAppProfileEntity): boolean => {
        return !!acceptMode || (!acceptMode && !routingId && !readOnly);
    };

    const onValidatedCallback = useCallback(
        async (valid: boolean) => {
            if (!valid || !formData) {
                return;
            }

            // Only send if valid and filled
            // send warning notification if invalid (valid if acceptMode is defined or if it is empty)
            if (formData.acceptedCardProfiles?.some((item) => !shouldSaveContractAppProfile(item))) {
                dispatch(
                    showWarningNotification({
                        text: i18n.t("contract.error.contractAppProfiles"),
                        autoCloseDisabled: true,
                    }),
                );
                return;
            }
            // only save contractAppProfile if it has acceptedMode defined, not if it is empty
            formData.acceptedCardProfiles = formData.acceptedCardProfiles?.filter((item) => !!item.acceptMode);

            const result = await saveFormData({ data: formData });

            const hasError = "error" in result;
            if (!hasError) {
                completeSubmit(
                    i18n.t("common.formSaveSuccess", { id: result.data.id, resource: "Contract Profile" }),
                    `${BASE_PATH}/${ResourceName.CONTRACT_PROFILES}/${FORM_ACTION.EDIT}/${result.data.id}`,
                );
            }
        },
        [saveFormData, formData, completeSubmit, dispatch],
    );

    return (
        <Spin spinning={saveFormIsInProgress || configIsFetching}>
            {formDefinition?.formConfiguration && (
                <FormManager<ContractProfileEntity>
                    name="ContractProfileCreateForm"
                    formData={formData}
                    formLayout={FormLayoutType.TABS}
                    formDefinition={formDefinition.formConfiguration}
                    onChange={onChangeCallback}
                    shouldValidate={runSubmit}
                    onValidated={onValidatedCallback}
                />
            )}
        </Spin>
    );
};
